import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Loader2, ShieldAlert, ShieldCheck, ShieldX, Clock } from "lucide-react";
import { api } from "@/api/client";

/**
 * Public certificate verification page (Phase 1e).
 *
 * Route: `/academy/verify/:code` — PUBLIC, no auth required.
 *
 * Renders 4 states cleanly:
 *  - valid    → certificate details from the immutable snapshot
 *  - revoked  → recipient/program + public reason (or canonical fallback)
 *  - expired  → recipient/program + expiration date
 *  - not_found → neutral "unable to verify" message; NEVER enumerate why
 *
 * IMPORTANT: All snapshot text is rendered via React's default text
 * pipeline. `dangerouslySetInnerHTML` is not used anywhere in this file.
 */
export default function CertificateVerify() {
  const { code } = useParams();
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/academy/certificates/verify/${encodeURIComponent(code || "")}`);
        if (!cancelled) setState({ status: "ready", data: r.data });
      } catch (err) {
        if (!cancelled) setState({ status: "ready", data: { status: "not_found" } });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const data = state.data || {};
  const s = data.status || "loading";

  return (
    <div data-testid="certificate-verify-page" className="min-h-screen bg-v2-bg text-v2-text flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full">
        <header className="text-center mb-8">
          <p className="text-[11px] uppercase tracking-[0.24em] text-v2-text-muted">Informed OS Academy</p>
          <h1 className="mt-1 text-[28px] font-semibold" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            Certificate Verification
          </h1>
        </header>

        {state.status === "loading" && (
          <div className="border border-v2-border rounded-lg p-10 text-center" data-testid="verify-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-v2-text-muted" />
          </div>
        )}

        {state.status === "ready" && s === "valid" && (
          <VerifyPanel data={data} kind="valid" />
        )}
        {state.status === "ready" && s === "revoked" && (
          <VerifyPanel data={data} kind="revoked" />
        )}
        {state.status === "ready" && s === "expired" && (
          <VerifyPanel data={data} kind="expired" />
        )}
        {state.status === "ready" && s === "not_found" && (
          <div
            data-testid="verify-not-found"
            className="border border-v2-border rounded-lg p-8 text-center bg-v2-surface"
          >
            <ShieldAlert className="w-10 h-10 mx-auto text-v2-text-muted mb-3" />
            <h2 className="text-lg font-medium mb-1">We couldn't verify this certificate</h2>
            <p className="text-sm text-v2-text-muted">
              The certificate reference you provided is not recognized. Please double-check the link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function VerifyPanel({ data, kind }) {
  const preset = {
    valid: {
      icon: ShieldCheck,
      color: "border-emerald-400/40 bg-emerald-400/5",
      accent: "text-emerald-400",
      label: "Valid Certificate",
      subtitle: "This certificate has been verified as authentic.",
    },
    revoked: {
      icon: ShieldX,
      color: "border-red-400/40 bg-red-400/5",
      accent: "text-red-400",
      label: "Revoked",
      subtitle:
        data.revoked_reason_public && data.revoked_reason_public.trim()
          ? data.revoked_reason_public
          : "This certificate has been revoked by the issuer.",
    },
    expired: {
      icon: Clock,
      color: "border-amber-400/40 bg-amber-400/5",
      accent: "text-amber-400",
      label: "Expired",
      subtitle: "This certificate's validity period has ended.",
    },
  }[kind];
  const Icon = preset.icon;
  return (
    <div data-testid={`verify-${kind}`} className={`border rounded-lg p-8 bg-v2-surface ${preset.color}`}>
      <div className="flex items-center gap-3 mb-6">
        <Icon className={`w-8 h-8 ${preset.accent}`} />
        <div>
          <div className={`text-xs uppercase tracking-widest ${preset.accent}`}>{preset.label}</div>
          <div className="text-sm text-v2-text mt-1">{preset.subtitle}</div>
        </div>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <Field label="Recipient" value={data.recipient_name} testid="verify-recipient" span={3} />
        <Field label="Program" value={data.program_title} testid="verify-program" span={3} />
        <Field label="Certificate #" value={data.certificate_number} testid="verify-number" />
        <Field label="Issued" value={fmt(data.issuance_date)} testid="verify-issued" />
        <Field label="Completed" value={fmt(data.completion_date)} testid="verify-completed" />
        <Field label="Issuer" value={data.issuer_name} testid="verify-issuer" span={3} />
        {data.signatory_name && (
          <Field label="Signatory" value={`${data.signatory_name} · ${data.signatory_title || ""}`} testid="verify-signatory" span={3} />
        )}
        {data.expires_at && (
          <Field label={kind === "expired" ? "Expired" : "Valid until"} value={fmt(data.expires_at)} testid="verify-expires" span={3} />
        )}
        {kind === "revoked" && data.revoked_at && (
          <Field label="Revoked on" value={fmt(data.revoked_at)} testid="verify-revoked-at" span={3} />
        )}
      </dl>
    </div>
  );
}

function Field({ label, value, testid, span = 1 }) {
  return (
    <div className={`sm:col-span-${span}`}>
      <dt className="text-v2-text-muted">{label}</dt>
      <dd data-testid={testid} className="text-v2-text mt-0.5 font-medium break-words">{value || "—"}</dd>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}
